import type { UserPromotion } from '@breakoutlearning/firebase-repository/models/UserPromotion'
import classNames from 'classnames'
import { InfoIcon } from 'components/icons/Info'
import { useBreakoutUser } from 'hooks/profile'
import { DateTime } from 'luxon'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'

export const UserPromotions = observer(function UserPromotionsDialog() {
  const { t } = useTranslation()
  const user = useBreakoutUser()
  const promotions = user.userPromotions.models
  return (
    <div className="flex h-full flex-col">
      <h2 className="text-headline-large">{t('profile.promotions')}</h2>
      <div className="mb-3 text-body-large text-core-on-tertiary">
        {t('profile.promotions_description')}
      </div>
      <div className="mt-6 flex h-full w-full flex-1 flex-col gap-2">
        {promotions.length === 0 && (
          <div className="flex h-full flex-1 flex-col items-center justify-center rounded-3xl bg-surface p-3">
            <InfoIcon className="fill-fixed-accent-color" size={32} />
            <div className="mt-1 text-headline-small text-core-on-tertiary">
              {t('profile.no_promotions_found')}
            </div>
            <div className="mt-2 text-center text-body-large text-core-on-tertiary">
              {t('profile.no_promotions')}
            </div>
          </div>
        )}
        {promotions.map((promotion) => (
          <UserPromotionRow key={promotion.id} promotion={promotion} />
        ))}
      </div>
    </div>
  )
})

const UserPromotionRow = observer(function UserPromotionRow({
  promotion,
}: {
  promotion: UserPromotion
}) {
  const { t } = useTranslation()
  const user = useBreakoutUser()
  const data = promotion.data
  const allRedemptions = user.userPromotionRedemptions.models
  const redemptions = allRedemptions.filter(
    (redemption) => redemption.data.userPromotionId === promotion.id
  )
  const consumed = redemptions.length
  const expirationDateTime = DateTime.fromJSDate(data.expirationDate)
  const isActive = promotion.isActive
  return (
    <div
      className="flex flex-row items-center justify-between rounded-2xl bg-surface p-5"
      tabIndex={0}
    >
      <div>
        <div
          className={classNames('text-label-small uppercase', {
            'text-core-error': !isActive,
            'text-core-success': isActive,
          })}
        >
          {isActive ? t('profile.active') : t('profile.inactive')}
        </div>
        <div className="text-label-medium">{data.promotionName}</div>
        <div className="text-body-small text-on-surface-var">
          {t('profile.expires_at', {
            expirationDate: expirationDateTime.toLocaleString(
              DateTime.DATE_FULL
            ),
          })}
        </div>
      </div>
      <div className="text-center">
        <div className="text-title-large text-on-surface">
          {consumed}/{data.consumableCount}
        </div>
        <div className="text-body-small text-grey-text">
          {t('profile.consumed')}
        </div>
      </div>
    </div>
  )
})
